// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Main listing component for the item triage center.
 * Displays a filterable table of items from external systems that need classification.
 */
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  CheckCircle,
  DoNotDisturbOnOutlined,
  ListRounded,
  PendingOutlined,
} from '@/constants/hardfin-standard-icons';
import { ItemTriageStatus } from '@/items/api/triage-types';
import { TriageTable } from '@/items/components/triage-table';
import { TriageTableSkeleton } from '@/items/components/triage-table-skeleton';
import { TriageSearchListing } from '@/items/hooks/use-triage-items';
import { Stack } from '@mui/material';
import { Suspense } from 'react';

interface TriageListingProps {
  currentStatus: ItemTriageStatus;
  searchParameters: TriageSearchListing;
  navigateFn: (options: { search: TriageSearchListing }) => void;
}

const STATUS_FILTER_OPTIONS: FilterOption<ItemTriageStatus>[] = [
  {
    value: 'all',
    label: 'All',
    icon: <ListRounded fontSize="small" />,
  },
  {
    value: 'NEEDS_REVIEW',
    label: 'Needs review',
    icon: <PendingOutlined fontSize="small" />,
  },
  {
    value: 'MATCHED',
    label: 'Matched',
    icon: <CheckCircle fontSize="small" />,
  },
  {
    value: 'DISMISSED',
    label: 'Dismissed',
    icon: <DoNotDisturbOnOutlined fontSize="small" />,
  },
];

/**
 * Converts ItemTriageStatus to URL search param value.
 * NEEDS_REVIEW -> undefined (no param), all -> 'all', MATCHED -> 'matched', DISMISSED -> 'dismissed'
 */
function toUrlStatus(
  status: ItemTriageStatus
): 'all' | 'matched' | 'dismissed' | undefined {
  if (status === 'all') {
    return 'all';
  }
  if (status === 'MATCHED') {
    return 'matched';
  }
  if (status === 'DISMISSED') {
    return 'dismissed';
  }
  // NEEDS_REVIEW = no param
  return undefined;
}

/**
 * Main listing component for the item triage center.
 */
export function TriageListing(props: TriageListingProps) {
  const { navigateFn, searchParameters, currentStatus } = props;

  /**
   * Handle status toggle change.
   * Resets page to 1 when filter changes.
   */
  const handleStatusChange = (newStatus: ItemTriageStatus) => {
    // Reset page when status changes
    const { page: _, ...restParams } = searchParameters;
    navigateFn({
      search: {
        ...restParams,
        status: toUrlStatus(newStatus),
      },
    });
  };

  /**
   * Handle page change from pagination controls.
   */
  const handlePageChange = (page: number) => {
    navigateFn({
      search: { ...searchParameters, page },
    });
  };

  return (
    <Stack>
      {/* Section 1: Filter toggle bar */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FilterToggleGroup
          value={currentStatus}
          options={STATUS_FILTER_OPTIONS}
          onChange={handleStatusChange}
          ariaLabel="item triage status filter"
        />
      </Stack>

      {/* Section 2: Table controls (provides spacing) */}
      <TableControlContainer>
        <Stack direction="row" marginRight="auto" />
      </TableControlContainer>

      {/* Section 3: Table */}
      <SupportErrorBoundary message="Failed to load triage items">
        <Suspense fallback={<TriageTableSkeleton />}>
          <TriageTable
            filter={{
              status: currentStatus,
              page: searchParameters.page,
            }}
            onPageChange={handlePageChange}
          />
        </Suspense>
      </SupportErrorBoundary>
    </Stack>
  );
}
